import consultationPreprocessingRequestsApi from "@/vue/api/consultation_preprocessing_requests";
import * as mutationTypes from "../mutation-types";

// =================================
// State Interfaces
// =================================
export interface ConsultationExtractedQuestionItem {
  id: number;
  question: string;
  quote: string | null;
}

export interface ConsultationAssessmentDraftIcd10BranchCandidate {
  id: number;
  name: string;
}

export interface ConsultationAssessmentDraft {
  complaint: string | null;
  urgency: "immediate" | "soon" | "watchful_waiting" | "no_assessment_needed" | null;
  body: string | null;
  icd10BranchCandidates: ConsultationAssessmentDraftIcd10BranchCandidate[];
}

export interface ConsultationPreprocessingRequestState {
  isComplete: boolean | null;
  questions: ConsultationExtractedQuestionItem[];
  assessmentDraft: ConsultationAssessmentDraft | null;
  status: ConsultationPreprocessingStatus;
  llmAssessmentStatus: LlmAssessmentStatus | null;
}

// フロントエンド側の状態管理用のステータス。主にpollingの制御に使う。
// バックエンドの集約ステータス(llmAssessmentStatus)とは別物だが、あえて対応づけるなら、以下になる。
// 
// fetchするまでは "idle"、fetch中は "fetching"、fetch後のステータスはバックエンドの集約ステータス(llmAssessmentStatus)に依存する。
// 一回fetchして:
// - "unprocessed", "processing" だった場合は、フロントエンド側では "polling" に遷移する。
// - "completed" だった場合は、フロントエンド側では "completed" に遷移する。
// - "failed" だった場合は、フロントエンド側では "failed" に遷移する。
export type ConsultationPreprocessingStatus =
  | "idle"
  | "polling"
  | "fetching"
  | "completed"
  | "failed";

// バックエンド(API)が返す いつでも相談の事前解析AIワークフロー全体のステータス
export type LlmAssessmentStatus =
  | "unprocessed"
  | "processing"
  | "completed"
  | "failed";

// =================================
// API Response Interfaces
// =================================
export interface ConsultationPreprocessingRequestResponse {
  llmAssessmentStatus: LlmAssessmentStatus;
  questions: ConsultationExtractedQuestionItem[];
  assessmentDraft: ConsultationAssessmentDraft | null;
}

export interface FetchParams {
  consultationId?: number | string;
}

type StoreCommit = (type: string, payload?: unknown) => void;

type ConsultationPreprocessingActionContext = {
  commit: StoreCommit;
};

// =================================
// 初期State
// =================================
const initialState = (): ConsultationPreprocessingRequestState => ({
  llmAssessmentStatus: null,
  isComplete: null,
  questions: [],
  assessmentDraft: null,
  status: "idle",
});

// =================================
// Storeの定義
// =================================
// -------------
// state
// -------------
const state: ConsultationPreprocessingRequestState = initialState();

// -------------
// getters
// -------------
const getters = {
  consultationExtractedQuestions: (state: ConsultationPreprocessingRequestState): ConsultationExtractedQuestionItem[] =>
    state.questions,
  consultationAssessmentDraft: (state: ConsultationPreprocessingRequestState): ConsultationAssessmentDraft | null =>
    state.assessmentDraft,
  consultationPreprocessingStatus: (state: ConsultationPreprocessingRequestState): ConsultationPreprocessingStatus =>
    state.status,
  consultationLlmAssessmentStatus: (state: ConsultationPreprocessingRequestState): LlmAssessmentStatus | null =>
    state.llmAssessmentStatus,
  hasExtractedQuestions: (state: ConsultationPreprocessingRequestState): boolean =>
    state.status === "completed" && state.questions.length > 0,
  hasAssessmentDraft: (state: ConsultationPreprocessingRequestState): boolean =>
    state.llmAssessmentStatus === "completed",
};

// -------------
// mutations
// -------------
const mutations = {
  [mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_REQUEST](
    state: ConsultationPreprocessingRequestState,
    payload: ConsultationPreprocessingRequestResponse
  ) {
    state.isComplete = payload.llmAssessmentStatus === "completed";
    state.questions = [...payload.questions];
    state.assessmentDraft = payload.assessmentDraft;
    state.llmAssessmentStatus = payload.llmAssessmentStatus;
  },
  [mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS](
    state: ConsultationPreprocessingRequestState,
    status: ConsultationPreprocessingStatus
  ) {
    state.status = status;
  },
  [mutationTypes.RESET_CONSULTATION_PREPROCESSING_REQUEST](
    state: ConsultationPreprocessingRequestState
  ) {
    Object.assign(state, initialState());
  },
};

// =================================
// actions
// =================================
const fetchConsultationPreprocessingRequest = async (
  { commit }: ConsultationPreprocessingActionContext,
  { consultationId }: FetchParams
): Promise<ConsultationPreprocessingRequestResponse | null> => {
  if (!consultationId) return null;

  commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "fetching");
  try {
    const response: ConsultationPreprocessingRequestResponse | undefined =
      await consultationPreprocessingRequestsApi.fetch({ consultationId });
    if (!response) {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
      return null;
    }

    commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_REQUEST, response);
    // バックエンドの集約ステータス(llmAssessmentStatus)を優先し、壁時計推測だけに依存しない
    const llmStatus = response.llmAssessmentStatus;
    if (llmStatus === "completed") {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "completed");
    } else if (llmStatus === "failed") {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
    } else {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "polling");
    }
    return response;
  } catch (error) {
    console.error("[ConsultationPreprocessingRequest] fetch failed", error);
    commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
    return null;
  }
};

const resetConsultationPreprocessingRequest = ({ commit }: ConsultationPreprocessingActionContext) => {
  commit(mutationTypes.RESET_CONSULTATION_PREPROCESSING_REQUEST);
};

const actions = {
  fetchConsultationPreprocessingRequest,
  resetConsultationPreprocessingRequest,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
